import * as Requests from "requests";
import * as Responses from "responses";
import * as Dtos from "dtos";
import { IOrderDTO } from "dtos";

export interface ListResult<T> {
	totalRecords: number;
	totalPages: number;
	page: number;
	limit: number;
	records: T[];
}

export class ApiError extends Error {
	constructor(public message: string, public HumanReadable: boolean, public statusCode: number) {
		super(message);
	}
	
	public readonly type : "ApiError" = "ApiError";
}

export function isApiError(error : unknown) : error is ApiError {
	if (error instanceof ApiError) {
		return true;
	}
	
	// When transpiled with Babel, and depending on the environment, the ApiError class may actually be removed and replaced with an object.
	// This causes error instanceof ApiError to return false, so we must also check if properties match. 
	const propNames: Array<keyof ApiError> = Object.getOwnPropertyNames(error) as any[];
	
	return (
		propNames.indexOf("HumanReadable") > -1 &&
		propNames.indexOf("message") > -1 &&
		propNames.indexOf("statusCode") > -1 &&
		propNames.indexOf("type") > -1 &&
		(error as ApiError).type === "ApiError"
	)
}

/**
 * Combines to pieces of a path, removing extra slashes from the middle. 
 */
function CombinePaths(left: string, right: string): string {
	const l = left.replace(/\/+$/, "");
	const r = right.replace(/^\/+/, "");
	
	return `${l}/${r}`;
}

export class BaseService {
	/**
	 * Builds a new BaseService.
	 */
	constructor(path: string, isFullPath = false) {
		this._BaseUrl = !isFullPath ? "/api/" + path + "/" : path;
	}

	/**
    The base URL for all requests.
    */
	private _BaseUrl: string;

	/**
	 * Checks if the status code is okay, i.e. between 200 and 299.
	 */
	protected isOkay: (statusCode: number) => boolean = statusCode => statusCode >= 200 && statusCode < 300;

	/**
    Builds headers for a request, including the Content-Type and Accept headers.
    */
	protected BuildRequestHeaders(contentType?: string, extraHeaders: { [key: string]: string } = {}) {
		const headers = {
			Accept: "application/json",
			...extraHeaders
		};

		return contentType ? { ...headers, "Content-Type": contentType } : headers;
	}

	/**
	 * Sends a request to the given path, serializing the data to JSON.
	 */
	protected CreateRequest: (
		method: "POST" | "PUT" | "GET" | "DELETE",
		path: string,
		data?: any
	) => Promise<Response> = (method, path, data) => {
	    const url = CombinePaths(this._BaseUrl, path);
		const baseOptions: RequestInit = {
			method: method,
			headers: {
				Accept: "application/json"
			},
		};

		if (method === "GET") {
			let queryParts : string [] = [];
			
			if (typeof data === "object") {
				for (let key in data) {
				    const value = data[key];
				    
				    if (value !== undefined && value !== null) {
                        const encodedValue = encodeURIComponent(value)
						queryParts.push(`${key}=${encodedValue}`)
					}
				}
			}		
			
			const query = queryParts.join("&")
			
			return fetch(`${url}?${query}`, baseOptions);
		}

		return fetch(url, {
			...baseOptions,
			body: JSON.stringify(data || {}),
			headers: {
				...baseOptions.headers,
				"Content-Type": "application/json"
			}
		});
	};

	/**
	 * Parses the response text from a fetch result into an ApiError object. Will throw an error if the response status code is between 200 and 299.
	 */
	protected ParseError: (response: Response) => Promise<ApiError> = async response => {
		if (this.isOkay(response.status)) {
			throw new Error(
				`Received an HTTP response that did not indicate an error (${response.status} ${response.statusText}). Unable to parse error details.`
			);
		}

		let contentTypeHeader: string = "";
		
		for (let [headerName, headerValue] of response.headers) {
			if (headerName && headerName.toLowerCase() === "content-type") {
				contentTypeHeader = headerValue && headerValue.toLowerCase() || "";
			}
		}
		
		const body = await response.text();
		let error: ApiError;

		if (/^application\/json/i.test(contentTypeHeader || "")) {
			let errorObject : { message?: string, errorMessage?: string }= JSON.parse(body);

			if (errorObject && (errorObject.message || errorObject.errorMessage)) {
			    const message = errorObject.message || errorObject.errorMessage || "Unknown error, missing `message` or `errorMessage` property.";
			    
				console.error(message, {body});
				
				error = new ApiError(message, true, response.status);
			} else {
				const message = `The request failed with ${response.status} ${response.statusText}. Client was unable to reliably parse the error message due to a missing \`message\` property.`;

				console.error(message, {body});
				
				error = new ApiError(message, true, response.status);
			} 
		} else {
			const message = `The request failed with ${response.status} ${response.statusText}, and returned an unexpected content-type header value of \"${contentTypeHeader}\".`;
			
			console.error(message, {body});
			
			error = new ApiError(message, true, response.status);
		}

		return error;
	};

	/**
	 * Sends a request to the given path and parses the response JSON.
	 */
	protected HandleFullRequest: <T>(
		method: "GET" | "PUT" | "POST" | "DELETE",
		path: string,
		data?: any
	) => Promise<T> = async (method, path, data) => {
		const response = await this.CreateRequest(method, path, data);

		if (this.isOkay(response.status)) {
			const body = await response.text();

			return JSON.parse(body);
		}

		const error = await this.ParseError(response);

		throw error;
	};
}

/**
 * A service for interacting with an account's Stages.
 */
export class StageService extends BaseService {
	constructor() {
		super("stages");
	}

	/**
	 * Gets a stage with the given id.
	 */
	public GetAsync = (id: number) => this.HandleFullRequest<Dtos.IStageDTO>("GET", id.toString());

	/**
	 * Returns a list of stages.
	 */
	public ListAsync = (status: "all" | "active" | "inactive" = "all") =>
		this.HandleFullRequest<Dtos.IStageDTO[]>("GET", "", {status : status});

	/**
	 * Creates a stage.
	 */
	public CreateAsync = (data: Requests.ICreateStageRequest) =>
		this.HandleFullRequest<Dtos.IStageDTO>("POST", "", data);

	/**
	 * Updates a stage.
	 */
	public UpdateAsync = (id: number, stage: Dtos.IStageDTO) =>
		this.HandleFullRequest<Dtos.IStageDTO>("PUT", id.toString(), stage);

	/**
	 * Deletes a stage.
	 */
	public DeleteAsync = (id: number) => this.HandleFullRequest<void>("DELETE", id.toString());
}

/**
 * A service for interacting with an account's Orders.
 */
export class OrderService extends BaseService {
	constructor() {
		super("orders");
	}

	/**
	 * Gets an order with the given id.
	 */
	public GetAsync = (id: number) => this.HandleFullRequest<Dtos.IOrderDTO>("GET", id.toString());

	/**
	 * Gets a breakdown of the stages for all orders on the account.
	 */
	public GetBreakdownAsync = (status?: "open" | "closed") =>
		this.HandleFullRequest<Dtos.IBreakdownDTO[]>("GET", "breakdown", {status: status});

	/**
	 * Gets an order based on its Shopify id.
	 */
	public GetByShopifyIdAsync = (id: number) => this.HandleFullRequest<Dtos.IOrderDTO>("GET", "shopify/" + id.toString());

	/**
	 * Returns a list of the order's line items from Shopify.
	 */
	public ListLineItemsAsync = (id: number) =>
		this.HandleFullRequest<Dtos.ILineItemDTO[]>("GET", id.toString() + "/lineitems");

	/**
	 * Returns a list of the order's history records.
	 */
	public ListRecordsAsync = (id: number) => this.HandleFullRequest<Dtos.IRecordDTO[]>("GET", id.toString() + "/history");

	/**
	 * Returns an list of orders.
	 */
	public ListAsync = (listOptions: {
		status?: string;
		stageId?: number;
		limit?: number;
		page?: number;
		sortBy?: string;
		sortDirection?: string;
	}) => this.HandleFullRequest<ListResult<Dtos.IOrderDTO>>("GET", "", listOptions);

	/**
	 * Creates an order.
	 */
	public CreateAsync = (order: Dtos.IOrderDTO) => this.HandleFullRequest<Dtos.IOrderDTO>("POST", "", order);

	/**
	 * Closes an order both in Stages and in Shopify.
	 */
	public CloseAsync = (id: number) => this.HandleFullRequest<Dtos.IArchiveDateDTO>("POST", id.toString() + "/close");

	/**
	 * Opens an order both in Stages and in Shopify.
	 */
	public OpenAsync = (id: number) => this.HandleFullRequest<Dtos.IArchiveDateDTO>("POST", id.toString() + "/open");

	/**
	 * Imports orders from Shopify and sets them to the given stage.
	 */
	public ImportAsync = (data: Requests.IImportRequest) =>
		this.HandleFullRequest<{ totalImported: number, importedOrder?: IOrderDTO }>("POST", "/import", data);

	/**
	 * Updates an order's team notes.
	 */
	public UpdateNotesAsync = (id: number, data: Requests.IUpdateOrderNotesRequest) =>
		this.HandleFullRequest<{teamNotes: string | null}>("PUT", `${id}/notes`, data);

	/**
	 * Updates an order's due date.
	 */
	public UpdateDueDateAsync = (id: number, data: Requests.IUpdateOrderDueDateRequest) =>
		this.HandleFullRequest<{dueDate: Date | string | null}>("PUT", `${id}/due-date`, data);

	/**
	 * Updates an order's team notes.
	 */
	public UpdateStageAsync = (id: number, data: Requests.IUpdateOrderStageRequest) =>
		this.HandleFullRequest<{record: Dtos.IStageRecordDTO}>("PUT", `${id}/stage`, data);

	/**
	 * Updates the stages of multiple orders.
	 */
	public BatchUpdateStageAsync(orderIds: Array<number> | ReadonlyArray<number>, stageId: number): Promise<Dtos.IOrderDTO[]> {
		const data = { orderIds, stageId };

		return this.HandleFullRequest<Dtos.IOrderDTO[]>("PUT", "batch/update-stage", data);
	}

	/**
	 * Opens multiple orders in both Stages and Shopify.
	 */
	public BatchOpenAsync = (orderIds: Array<number> | ReadonlyArray<number>) =>
		this.HandleFullRequest<Dtos.IOrderDTO[]>("POST", "batch/open", { orderIds });

	/**
	 * Closes multiple orders in both Stages and Shopify.
	 */
	public BatchCloseAsync = (orderIds: Array<number> | ReadonlyArray<number>) =>
		this.HandleFullRequest<Dtos.IOrderDTO[]>("POST", "batch/close", { orderIds });

	/**
	 * Deletes an order.
	 */
	public DeleteAsync = (id: number) => this.HandleFullRequest<void>("DELETE", id.toString());

    /**
     * Searches for a batch of orders.
     */
    public SearchAsync = (ids: string[]) => 
        this.HandleFullRequest<Responses.ISearchResponse>("GET", "search", { ids: ids.join(",") });
    
}

export class CustomerService extends BaseService {
	constructor() {
		super("customers");
	}

	/**
	 * Returns a list of orders from this customer.
	 * TEMP: This method temporarily expects an orderId and ignores the customer id. The customer will be looked up according to the orderId.
	 */
	public ListOrdersAsync = (id: number, orderId: number) =>
		this.HandleFullRequest<Dtos.IOrderDTO[]>("GET", id.toString() + "/orders", { orderId: orderId });
}

export class TeamService extends BaseService {
	constructor() {
		super("team");
	}

	/**
	 * Attempts to accept an account invitation.
	 */
	public AcceptInviteAsync = (inviteId: number, data: Requests.IAcceptInviteRequest) =>
		this.HandleFullRequest<{}>("POST", `${inviteId}/accept`, data);

	/**
	 * Creates and sends a team invite.
	 */
	public InviteMemberAsync = (data: Requests.ICreateInviteRequest) =>
		this.HandleFullRequest<Dtos.ITeamMemberDTO>("POST", "", data);

	/**
	 * Deletes a team member or invite.
	 */
	public DeleteMemberAsync = (memberId: string) => this.HandleFullRequest<{}>("DELETE", `${memberId}`);
}

export class UserService extends BaseService {
	constructor() {
		super("users");
	}

	/**
	 * Creates a new user account and logs the user in via cookie auth.
	 */
	public CreateAsync = (data: Requests.ICreateUserRequest) => this.HandleFullRequest<{}>("POST", "", data);

	/**
	 * Updates a user's login details including fullname, username and password.
	 */
	public UpdateLoginDetailsAsync = (details: Requests.IUpdateRequest) =>
		this.HandleFullRequest<{}>("PUT", `login-details`, details);

	/**
	 * Updates a user's login password.
	 */
	public UpdatePasswordAsync = (details: Requests.IUpdatePasswordRequest) =>
		this.HandleFullRequest<{}>("PUT", `password`, details);

	/**
	 * Generates an OAuth URL.
	 */
	public GenerateOAuthUrlAsync = (service: "shopify", data: Requests.IGenerateOAuthUrlRequest) =>
		this.HandleFullRequest<{ oauthUrl: string }>("POST", `${service}/generate-oauth-url`, data);

	/**
	 * Updates the user's default service stages.
	 */
	public UpdateDefaultStagesAsync = (service: string, data: Requests.IUpdateDefaultStagesRequest) =>
		this.HandleFullRequest<{}>("PUT", `${service}/default-stages`, data);

	/**
	 * Cancels a user's Stripe or Shopify subscription charge.
	 */
	public CancelSubscriptionAsync = (data: Requests.ICancelAccountRequest) =>
		this.HandleFullRequest<{}>("POST", `cancel`, data);

	/**
	 * Updates a user's subscription plan.
	 * @returns A ConfirmationUrl that the user must be sent to where they can accept or decline the change.
	 */
	public UpdatePlanAsync = (data: Requests.IUpdatePlanRequest) =>
		this.HandleFullRequest<Requests.IUpdatePlanResponse>("PUT", `plan`, data);

	/**
	 * Updates a user's credit card.
	 */
	public UpdateCardAsync = (data: Requests.IUpdateCardRequest) => this.HandleFullRequest<{}>("PUT", `card`, data);

    /**
     * Toggles the setting for allowing customers to search via order number.
     */
    public ToggleAllowWidgetSearchViaOrderNumber = (data: Requests.IToggleAllowWidgetSearchViaOrderNumberRequest) => 
		this.HandleFullRequest<{}>("PUT", `widget/toggle-search-via-order-number`, data);
}

export class RuleService extends BaseService {
	constructor() {
		super("rules");
	}

	/**
	 * Creates a new automation rule.
	 */
	public CreateAsync = (data: Requests.ICreateRuleRequest) => this.HandleFullRequest<Responses.ICreateOrUpdateRuleResponse>("POST", "", data);

	/**
	 * Gets an automation rule.
	 */
	public GetAsync = (id: number) => this.HandleFullRequest<Dtos.IAutomationRuleDTO>("GET", `${id}`);

	/**
	 * Lists automation rules.
	 */
	public ListAsync = () => this.HandleFullRequest<Dtos.IAutomationRuleDTO[]>("GET", "");

    /**
     * Updates a rule's name.
     */
    public UpdateNameAsync = (id: number, data: Requests.IUpdateRuleNameRequest) =>
		this.HandleFullRequest<Responses.ICreateOrUpdateRuleResponse>("PUT", `${id}/name`, data);

    /**
     * Updates a rule's paused status.
     */
    public UpdatePausedStatusAsync = (id: number, data: Requests.IUpdateRulePausedStatusRequest) =>
		this.HandleFullRequest<Responses.ICreateOrUpdateRuleResponse>("PUT", `${id}/status`, data);

	/**
	 * Deletes an automation rule.
	 */
	public DeleteAsync = (id: number) => this.HandleFullRequest<{}>("DELETE", `${id}`);

	/**
	 * Updates the actions on an automation rule.
	 */
	public UpdateActionsAsync = (id: number, data: Requests.IUpdateActionsRequest) => 
		this.HandleFullRequest<Responses.ICreateOrUpdateRuleResponse>("PUT", `${id}/actions`, data);

	/**
	 * Updates the triggers on an automation rule.
	 */
	public UpdateTriggersAsync = (id: number, data: Requests.IUpdateTriggersRequest) => 
		this.HandleFullRequest<Responses.ICreateOrUpdateRuleResponse>("PUT", `${id}/triggers`, data);
	
	/**
	 * Previews email content.
	 */
	public PreviewEmailAsync = (ruleId: number, data: Requests.IPreviewEmailActionRequest) =>
		this.HandleFullRequest<Responses.IPreviewEmailContentResponse>("POST", `${ruleId}/email-actions/preview`, data);
}

export class EmbedService extends BaseService {
	constructor(private service = "shopify") {
		super(`embed/${service}`, true);

		if (service !== "shopify") {
			throw new Error(`${service} not recognized as valid embed service.`);
		}
	}

	/**
	 * Enables the embedded widget on the given service.
	 */
	public EnableAsync = () => this.HandleFullRequest<{}>("PUT", `enable`);

	/**
	 * Disables the embedded widget on the given service.
	 */
	public DisableAsync = () => this.HandleFullRequest<{}>("PUT", `disable`);
}

export class ProxyService extends BaseService {
	constructor(props: {shopDomain: string, hostDomain: string, proxyPathPrefix: string}) {
		super(CombinePaths(props.proxyPathPrefix, "/api"), true);
	}
	
	/**
	 * Lists Shopify order ids for the given search term/type combination.
	 */
	public ListCustomerOrdersAsync (searchTerm: string, searchType: "email" | "order") {
        const url = searchType === "email" 
            ? `customers/${encodeURIComponent(searchTerm)}/orders` 
            : `orders/search/${encodeURIComponent(searchTerm)}`;
		
        return this.HandleFullRequest<Responses.IProxySearchResponse[]>("GET", url);
    }

    /**
     * Gets data and timeline records for the given Shopify order.
     */
    public GetOrderAsync = (shopifyOrderId: number) => 
        this.HandleFullRequest<Responses.IProxyGetOrderResponse>("GET", `orders/${shopifyOrderId}`);

	/**
	 * Gets the timeline records for the given Shopify order.
	 */
	public GetTimelineAsync = (shopifyOrderId: number) =>
		this.HandleFullRequest<Dtos.IStageRecordDTO[]>("GET", `orders/${shopifyOrderId}/timeline`);
}
